import { Component, OnDestroy, OnInit, Renderer2, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { AgendaService } from 'src/app/services/agenda/agenda.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { HeaderCommunicationService } from 'src/app/services/node/header-communication.service';
import { AgendaPeriode } from 'src/app/utils/interfaces/new/AgendaPeriode';
import { Event } from 'src/app/utils/interfaces/old/agenda-interface';
import { Header } from 'src/app/utils/interfaces/new/header.interface';
import { RessourceCreationRendezVous } from 'src/app/utils/interfaces/new/RessourceCreationRendezVous';
import { EventResource, SuperTypeEnum } from 'src/app/utils/interfaces/new/event-resource.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { CreationRendezVous } from 'src/app/utils/interfaces/new/CreationRendezVous';
import { RecurrenceService, FinRecurrenceTypeEnum, UniteDeTempsEnum } from './recurrence.service';
import { CreationEnConflit, ReasonEnum } from 'src/app/utils/interfaces/new/CreationEnConflit';
import { CreneauxEnConflit } from 'src/app/utils/interfaces/new/CreneauxEnConflit';
import { EventConflictComponent } from '../event-conflict/event-conflict.component';

const DATE_FORMAT = 'DD/MM/YYYY';
const HOUR_FORMAT = 'HH:mm:ss';

interface EventsTableDisplay {
  eventDate: Date;
  eventHour: string;
  rootEvent: Event;
  newEvent: CreationRendezVous;
}

@Component({
  selector: 'app-day-week-as-model',
  templateUrl: './day-week-as-model.component.html',
  styleUrls: ['./day-week-as-model.component.scss']
})
export class DayWeekAsModelComponent implements OnInit, OnDestroy {

  public formGroup: FormGroup
  public headerData = '';
  public type: 'day' | 'week';
  public date: Date;
  public reccurUnityList: Array<string> = ['semaine(s)', 'mois', 'an(s)'];
  public daysInWeek = [
    { label: 'L', value: 0 },
    { label: 'M', value: 1 },
    { label: 'M', value: 2 },
    { label: 'J', value: 3 },
    { label: 'V', value: 4 },
    { label: 'S', value: 5 },
    { label: 'D', value: 6 }
  ];
  public reccurUnityOffset = 1;
  public events: Paginated<EventsTableDisplay>;
  public eventsColumnTable = [
    { title: 'Date', data: 'eventDate' },
    { title: 'Heure', data: 'eventHour' },
    { title: 'Supprimer', data: null }
  ];

  private resource: RessourceCreationRendezVous;
  private _events: Array<Event>;

  constructor(
    private _modalSrv: ModalService,
    private _viewContainerRef: ViewContainerRef,
    private _formBuilder: FormBuilder,
    private _activatedRoute: ActivatedRoute,
    private _send: HeaderCommunicationService,
    private _agendaSrv: AgendaService,
    private _reccurSrv: RecurrenceService,
    private _renderer: Renderer2,
    private _router: Router
  ) {
    moment.locale('fr');

    // On récupère les paramètres de route
    const type = this._activatedRoute.snapshot.queryParamMap.get('type');
    const dateStr = this._activatedRoute.snapshot.queryParamMap.get('date');
    const resourceId = this._activatedRoute.snapshot.queryParamMap.get('resourceId');
    const resourceType = this._activatedRoute.snapshot.queryParamMap.get('resourceType');

    if (!resourceId || !resourceType) {
      throw Error('ResourceId et/ou resourceType manquant');
    } else {
      this.resource = {
        id: resourceId,
        type: resourceType
      }
    }

    if (!dateStr || dateStr === '') {
      throw Error('Date manquante');
    } else {
      this.date = moment(dateStr).toDate();
    }

    if (!type || type === '') {
      throw Error('Type semaine ou jour manquant');
    } else if (type && type !== 'day' && type !== 'week')  {
      throw Error('Le type dois être \'day\' ou \'week\'');
    } else {
      this.type = type as 'day' | 'week';
    }
    
    const head: Header = {
      backPage: 'agenda',
      isBack: true,
      title: `Répétition d\'une ${this.type === 'day' ? 'journée' : 'semaine'}`
    };
    this._send.announceHeader(head);
  }

  async ngOnInit(): Promise<void> {
    await this._initData();
  }

  async ngOnDestroy(): Promise<void> {
    this._agendaSrv.doRemoveNavigationParams();
  }

  public async navigateToPrevious(): Promise<void> {
    this.date = moment(this.date).subtract(1, 'month').toDate();
    try {
      setTimeout(async() => {
        await this._initPeriodes();
      }, 0);
    } catch (e) {
      this._showAlert();
    }
  }

  public async navigateToNext(): Promise<void> {
    this.date = moment(this.date).add(1, 'month').toDate();
    try {
      setTimeout(async() => {
        await this._initPeriodes();
      }, 0);
    } catch (e) {
      this._showAlert();
    }
  }

  public dayClicked(event: any): void {
    if (moment(event.day.date).isSameOrBefore(moment(), this.type as 'day' || 'week')) {
      return;
    }

    for (const e of this._events) {
      const eventStart = moment(event.day.date);
      const eventEnd = moment(event.day.date);

      eventStart.day(moment(this.type === 'day' ? event.day.date : e.dateDebut).day());
      eventStart.hour(moment(e.dateDebut).hour());
      eventStart.minute(moment(e.dateDebut).minute());
      eventStart.second(moment(e.dateDebut).second());

      // Dans le cas d'un rendez-vous de moins d'une journée on applique la même condition que pour la date de début
      if (moment(event.day.date).isSame(e.dateFin, 'day')) {
        eventEnd.day(moment(this.type === 'day' ? event.day.date : e.dateFin).day());
      } else {
        // Dans le cas d'un rendez-vous sur plusieurs jours, on calcul la différence de jours entre le début et la fin et on ajoute cette différence à la date de début pour obtenir la date de fin
        const diff = Math.abs(moment(e.dateDebut).diff(e.dateFin, 'day'));
        eventEnd.day(eventStart.day()).add(diff, 'day');
      }
      eventEnd.hour(moment(e.dateFin).hour());
      eventEnd.minute(moment(e.dateFin).minute());
      eventEnd.second(moment(e.dateFin).second());

      if (!this.events || !this.events.objects.find(ee => this._isSameDatetime(moment(ee.eventDate).format(DATE_FORMAT), ee.eventHour, eventStart.toDate()) )) {
        this._addEventToList({
          dateDebut: eventStart.toDate(),
          dateFin: eventEnd.toDate()
        }, e);
      }
    }
  }

  public generateEvents(): void {
    const reccurUnityTmp = this.formGroup.get('reccurUnity').value;
    let reccurUnity: UniteDeTempsEnum;
    if (reccurUnityTmp === 'semaine(s)') {
      reccurUnity = UniteDeTempsEnum.Week;
    } else if (reccurUnityTmp === 'mois') {
      reccurUnity = UniteDeTempsEnum.Month;
    } else if (reccurUnityTmp === 'an(s)') {
      reccurUnity = UniteDeTempsEnum.Year;
    }

    const reccurOffset = this.formGroup.get('reccurOffset').value;
    const reccurType = this.formGroup.get('reccurType').value;
    const reccurTypeIteration = this.formGroup.get('reccurTypeIteration').value;
    let reccurTypeDate = this.formGroup.get('reccurTypeDate').value;
    const daysIteration = this.formGroup.get('dayOfWeek').value;

    if (reccurType === 'iteration' && (!reccurTypeIteration || reccurTypeIteration < 1)) {
      this._modalSrv.openAlert(this._viewContainerRef, 'Erreur de saisie', 'La répétition ne peut pas être inférieur à 1');
      return;
    }

    if (reccurType === 'to-date' && !reccurTypeDate) {
      this._modalSrv.openAlert(this._viewContainerRef, 'Erreur de saisie', 'Une date de fin de répétition est obligatoire');
      return;
    } else if (reccurType === 'to-date' && moment(reccurTypeDate).isSameOrBefore(moment(), 'day')) {
      this._modalSrv.openAlert(this._viewContainerRef, 'Erreur de saisie', 'La date de fin de répétition doit être supérieur à aujourd\'hui');
      return;
    }

    if (reccurType === 'to-date') {
      if (reccurUnity === UniteDeTempsEnum.Week && moment(reccurTypeDate).day() !== moment(reccurTypeDate).endOf('week').day()) {
        reccurTypeDate = moment(reccurTypeDate).startOf('week').subtract(1, 'day').toDate();
      }

      if (reccurUnity === UniteDeTempsEnum.Month && moment(reccurTypeDate).day() !== moment(reccurTypeDate).endOf('month').day()) {
        reccurTypeDate = moment(reccurTypeDate).startOf('month').subtract(1, 'day').toDate();
      }

      if (reccurUnity === UniteDeTempsEnum.Year && moment(reccurTypeDate).day() !== moment(reccurTypeDate).endOf('year').day()) {
        reccurTypeDate = moment(reccurTypeDate).startOf('year').subtract(1, 'day').toDate();
      }
    }

    const events: Array<{ dateStart: Date, dateEnd: Date, event: Event }> = [];
    for (const e of this._events) {
      const eventsTmp = this._reccurSrv.findRecurrenceEventsDate({
        dateDebutInitiale: e.dateDebut,
        dateFinInitiale: e.dateFin,
        delais: reccurOffset,
        finRecurrence: {
          type: reccurType === 'iteration' ? FinRecurrenceTypeEnum.OcurrenceNumber : FinRecurrenceTypeEnum.Date,
          date: reccurTypeDate,
          nombreOccurrence: reccurTypeIteration
        },
        uniteDeTemps: reccurUnity,
        parametresSemaine: {
          uniteSemaineJoursDeLaSemaine: daysIteration
        }
      }, this.type);

      for (const ee of eventsTmp) {
        events.push({
          dateStart: ee.dateStart,
          dateEnd: ee.dateEnd,
          event: e
        });
      }
    }

    events.sort((a, b) => { return moment(a.dateStart).valueOf() - moment(b.dateStart).valueOf() });
    
    for (const e of events) {
      if (!this.events || !this.events.objects.find(ee => this._isSameDatetime(moment(ee.eventDate).format(DATE_FORMAT), ee.eventHour, e.dateStart) )) {
      this._addEventToList({
          dateDebut: e.dateStart,
          dateFin: e.dateEnd
        }, e.event);
      }
    }
  }

  public removeEvent(event: any): void {
    this.events.objects.splice(this.events.objects.findIndex(e => e === event), 1);
  }

  public addDay(day: { label: string, value: string }) {
    const days = this.formGroup.get('dayOfWeek').value;
    days.push(day.value);
    this.formGroup.get('dayOfWeek').patchValue(days);
  }

  public removeDay(day: { label: string, value: string }) {
    const days = this.formGroup.get('dayOfWeek').value;

    const dayIndex = days.indexOf(day.value);

    if (dayIndex > -1) {
      days.splice(dayIndex, 1);
    }

    this.formGroup.get('dayOfWeek').patchValue(days);
  }

  public cancelCreation(): void {
    if (this.events && this.events.objects && this.events.objects.length > 0) {
      const modal = this._modalSrv.openModalConfirmation(this._viewContainerRef, 'Génération en cours', 'Les rendez-vous de la liste ne sont pas créés. Si vous quittez, votre sélection sera perdue. Voulez-vous vraiment quitter et revenir à l\'Agenda ?');
      const sub = modal.instance.closeEmitter.subscribe((res) => {
        if (res) {
          this._navigateToAgenda();
        }
        sub.unsubscribe();
      });
    } else {
      this._navigateToAgenda();
    }
  }

  private _navigateToAgenda(): void {
    this._router.navigateByUrl('/user/agenda');
  }

  public async validateCreation(): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      const result = await this._doValidateCreation();
      if (result && result.length > 0) {
        // On ouvre une pop-up affichant les conflits
        const modalPayload: Array<CreneauxEnConflit> = [];
        for (const c of result) {
          modalPayload.push(c);
        }
        const modal = this._modalSrv.openModal(this._viewContainerRef, EventConflictComponent);
        modal.instance.explication = 'Plusieurs des rendez-vous générés entrent en conflit avec d’autres rendez-vous ou périodes existants dont voici la liste';
        modal.instance.conflictsList = modalPayload;
        modal.instance.cancelLabel = 'Annuler et revenir à l’édition';
        modal.instance.validateLabel = 'Enregistrer malgré tout l’ensemble des rendez-vous';
        const sub = modal.instance.closeEmitter.subscribe(async (rtd) => {
          if (rtd) {
            await this._doCreateEvents(true);
          }
          modal.destroy();
          sub.unsubscribe();
        });
      } else {
        // On lance la création des rendez-vous
        await this._doCreateEvents(false);
      }
      this._navigateToAgenda();
    } catch (e) {
      throw e;
    } finally {
      this._modalSrv.close(loading);
    }
  }

  private async _doCreateEvents(doForce = false): Promise<void> {
    return new Promise<void>(async(resolve, reject) => {
      try {
        if (this.events && this.events.objects && this.events.objects.length > 0) {
          const keepPatient = this.formGroup.get('optionKeepPatient').value;
          const keepDescription = this.formGroup.get('optionKeepDescription').value;

          for (const e of this.events.objects) {
            const event: CreationRendezVous = {
              dateDebut: e.newEvent.dateDebut,
              dateFin: e.newEvent.dateFin,
              ressoursesId: this.getResourcesInfos(e.rootEvent.ressourses),
              patientId: keepPatient && e.rootEvent.patient ? e.rootEvent.patient.id : undefined,
              description: keepDescription && e.rootEvent.description ? e.rootEvent.description : undefined,
              planifie: true,
              typeId: e.rootEvent.type ? e.rootEvent.type.id : undefined
            };

            await this._agendaSrv.createEvent(event, doForce);
          }
        }
        resolve();
      } catch (e) {
        reject(e);
      }
    });
  }

  private async _doValidateCreation(): Promise<Array<CreneauxEnConflit>> {
    return new Promise<Array<CreneauxEnConflit>>(async(resolve, reject) => {
      const eventConflicts: Array<CreneauxEnConflit> = [];

      try {
        if (this.events && this.events.objects && this.events.objects.length > 0) {
          for (const e of this.events.objects) {
            const result = await this._dryrunCreation({
              dateDebut: e.newEvent.dateDebut,
              dateFin: e.newEvent.dateFin,
              ressoursesId: this.getResourcesInfos(e.rootEvent.ressourses)
            });

            if (result) {
              for (const p of result) {
                eventConflicts.push(p.payload);
              }
            }
          }
        }

        resolve(eventConflicts);
      } catch (e) {
        reject(e);
      }
    });
  }

  private getResourcesInfos(resources: Array<EventResource>): Array<RessourceCreationRendezVous> {
    const rtd: Array<RessourceCreationRendezVous> = [];
    for (const r of resources) {
      rtd.push({ id: r.id, type: r.superType });
    }
    return rtd;
  }

  private async _dryrunCreation(event: CreationRendezVous): Promise<void | Array<CreationEnConflit>> {
    return new Promise<void | Array<CreationEnConflit>>(async(resolve, reject) => {
      try {
        await this._agendaSrv.createEvent(event, false, true);
        resolve();
      } catch (e) {
        if (e.status === 409) {
          resolve(e.error);
        } else {
          reject(e);
        }
      }
    });
  }

  private _isSameDatetime (displayDate: string, displayHour: string, newDateStart: Date) {
    const dateToTest = moment(displayDate, DATE_FORMAT);
    dateToTest.hour(Number(displayHour.split(':')[0]));
    dateToTest.minute(Number(displayHour.split(':')[1]));
    return dateToTest.isSame(newDateStart);
  }

  private async _initData(): Promise<void> {
    const loading = this._modalSrv.openLoading(this._viewContainerRef);

    try {
      // On initialise le header de la page
      this._initHeader();

      // On récupère les rendez-vous copiables
      this._events = await this._getEvents();

      // On initialise le formulaire
      this._initForm();

      // On applique les périodes au calendrier
      await this._initPeriodes();
    } catch (e) {
      this._showAlert();
    } finally {
      this._modalSrv.close(loading);
    }
  }

  private _initHeader(): void {
    if (this.type === 'day') {
      this.headerData = `Journée du ${moment(this.date).format('dddd DD MMMM YYYY')}`;
    } else {
      this.headerData = `Semaine du ${moment(this.date).startOf('week').format('dddd DD MMMM YYYY')} au ${moment(this.date).endOf('week').format('dddd DD MMMM YYYY')}`;
    }
  }

  private _initForm(): void {
    this.formGroup = this._formBuilder.group({
      repeatType: ['dates'],
      reccurUnity: [this.reccurUnityList[0]],
      reccurOffset: [1],
      reccurType: ['iteration'],
      reccurTypeIteration: [1],
      reccurTypeDate: [],
      dayOfWeek: [[]],
      optionKeepPatient: [false],
      optionKeepDescription: [false]
    });
  }

  private async _getEvents(): Promise<Array<Event>> {
    return new Promise<Array<Event>>(async(resolve, reject) => {
      try {
        const start = moment(this.date).startOf(this.type as 'day' | 'week').toDate();
        const end = moment(this.date).endOf(this.type as 'day' | 'week').toDate();
        const events = await this._agendaSrv.getEvents(start, end, { id: this.resource.id, superType: this.resource.type as SuperTypeEnum, nom: '', responsableId: '', type: '' });
        resolve(events);
      } catch(e) {
        reject(e);
      }
    });
  }

  private _addEventToList(event: CreationRendezVous, rootEvent: Event): void {
    if (this.events) {
      this.events.objects.push({
        eventDate: event.dateDebut,
        eventHour: moment(event.dateDebut).format(HOUR_FORMAT),
        rootEvent: rootEvent,
        newEvent: event
      });
    } else {
      this.events = {
        objects: [{
          eventDate: event.dateDebut,
          eventHour: moment(event.dateDebut).format(HOUR_FORMAT),
          rootEvent: rootEvent,
          newEvent: event
        }],
        page: 0, perpage: 0, total: 0
      };
    }
  }

  private async _initPeriodes() {
    const CALENDAR_VIEW_MONTH_DATE_FORMAT = 'dddd MMMM D';
    const CALENDAR_VIEW_MONTH_DATE_FORMAT_WITH_YEAR = 'dddd MMMM D YYYY';

    try {
      const getShowDays = async(): Promise<NodeListOf<Element>> => {
        return new Promise<NodeListOf<Element>>(async(resolve) => {
          const nodes = document.querySelectorAll('mwl-calendar-month-cell');
          if (!nodes || nodes.length === 0) {
            setTimeout(async() => {
              resolve(await getShowDays());
            }, 200);
          } else {
            resolve(nodes);
          }
        });
      }

      const getDaysOfRecurrentPeriodes = (periodes: Array<AgendaPeriode>): Array<number> => {
        const rtd: Array<number> = [];
        for (const p of periodes) {
          if (p.estRecurrent && !rtd.includes(p.jourRecurrence)) {
            rtd.push(p.jourRecurrence)
          }
        }
        return rtd;
      }

      const getDatesOfNonRecurrentPeriodes = (periodes: Array<AgendaPeriode>): Array<string> => {
        const rtd: Array<string> = [];
        for (const p of periodes) {
          const dateStr = moment(p.periodeDateDebut).format(CALENDAR_VIEW_MONTH_DATE_FORMAT);
          if (!p.estRecurrent && !rtd.includes(dateStr)) {
            rtd.push(dateStr)
          }
        }
        return rtd;
      }

      const applyPeriodesToCalendar = async (periodesNumber: Array<number>,periodesString: Array<string>): Promise<void> => {
        const rows = document.querySelectorAll('.cal-cell-row');
        rows.forEach((r) => {
          r.childNodes.forEach((e, k) => {
            if ((e as Element).tagName === 'MWL-CALENDAR-MONTH-CELL' && (periodesNumber.includes(k) || periodesString.includes((e as Element).querySelector('.cal-cell-top').getAttribute('aria-label')))) {
              this._renderer.addClass(e, 'periode-on');
            }
          });
        });
      }

      const getEnglishDayOrMonth = (str: string): string => {
        switch (str) {
          case 'janvier':
            return 'January';
          case 'février':
            return 'February';
          case 'mars':
            return 'March';
          case 'avril':
            return 'April';
          case 'mai':
            return 'May';
          case 'juin':
            return 'June';
          case 'juillet':
            return 'July';
          case 'août':
            return 'August';
          case 'septembre':
            return 'September';
          case 'octobre':
            return 'October';
          case 'novembre':
            return 'November';
          case 'décembre':
            return 'December';
          case 'lundi':
            return 'Monday';
          case 'mardi':
            return 'Tuesday';
          case 'mercredi':
            return 'Wednesday';
          case 'jeudi':
            return 'Thursday';
          case 'vendredi':
            return 'Friday';
          case 'samedi':
            return 'Saturday';
          case 'dimanche':
            return 'Sunday';
          default:
            return str;
        }
      };

      const getYearForPeriodes = (month: string): string => {
        if (month === 'janvier' && moment(this.date).format('MMMM') === 'December') {
          return moment(this.date).add(1, 'year').format('YYYY');
        } else if (month === 'décembre' && moment(this.date).format('MMMM') === 'January') {
          return moment(this.date).subtract(1, 'year').format('YYYY');
        } else {
          return moment(this.date).format('YYYY');
        }
      };

      const elements = await getShowDays();
      let start = elements[0].querySelector('.cal-cell-top').getAttribute('aria-label');
      let end = elements[elements.length - 1].querySelector('.cal-cell-top').getAttribute('aria-label');

      const startArr = start.split(' ');
      start = [
        getEnglishDayOrMonth(startArr[0]),
        getEnglishDayOrMonth(startArr[1]),
        startArr[2],
        getYearForPeriodes(startArr[1])
      ].join(' ');

      const endArr = end.split(' ');
      end = [
        getEnglishDayOrMonth(endArr[0]),
        getEnglishDayOrMonth(endArr[1]),
        endArr[2],
        getYearForPeriodes(endArr[1])
      ].join(' ');

      const dateStart = moment(start, CALENDAR_VIEW_MONTH_DATE_FORMAT_WITH_YEAR).toDate();
      const dateEnd = moment(end, CALENDAR_VIEW_MONTH_DATE_FORMAT_WITH_YEAR).toDate();

      // On récupère les périodes des jours affichés
      const periodes = await this._agendaSrv.getAgendaPeriodes(this.resource.id, this.resource.type, dateStart, dateEnd);
      const recurDays = getDaysOfRecurrentPeriodes(periodes);
      const nonRecurDays = getDatesOfNonRecurrentPeriodes(periodes);

      // On manipule le DOM pour modifier la couleur des jours étant sur une période
      applyPeriodesToCalendar(recurDays, nonRecurDays);

    } catch (e) {
      throw e;
    }
  }

  private _showAlert(msg?: string): void {
    msg = msg ? msg : 'Une erreur est survenue lors de la récupération des informations de cette page';

    this._modalSrv.openAlert(this._viewContainerRef, 'Erreur', msg);
  }

}
